import React, { useCallback, useEffect, useState } from 'react';
import { Bookmark, Search } from 'react-bootstrap-icons';

import { Col, ColProps, Input, Row, Select, Space, Typography } from 'antd';
import { Gutter } from 'antd/lib/grid/row';

import { useDebounce } from 'usehooks-ts';

import EmptyState from '@/components/shared/EmptyState';
import PageTitle from '@/components/shared/PageTitle';
import SkeletonBaseCard from '@/components/shared/SkeletonBaseCard';
import Wrapper from '@/components/shared/Wrapper';
import { useWindowScrollReachedEnd } from '@/hooks/useWindowScrollReachedEnd';
import { IPaginationParams } from '@/interfaces/pagination';
import { useGetAllSavedLessonsQuery } from '@/services/savedLessons';
import styled, { StyledProp } from '@/styled';

import Card from './Card';

const DEFAULT_ROWS_SPACING = [16, 16] as [Gutter, Gutter];

const DEFAULT_COLUMNS_WIDTH: Pick<ColProps, 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'> = {
  xs: 24,
  sm: 12,
  md: 12,
  lg: 8,
  xl: 6,
  xxl: 6
};

const SavedLessons = (props: StyledProp) => {
  const [filters, setFilters] = useState<Partial<IPaginationParams>>({ page: 1, size: 100 });

  const debouncedSearch = useDebounce(filters, 600);
  const reachedEnd = useWindowScrollReachedEnd();

  const { data: response, isFetching } = useGetAllSavedLessonsQuery(debouncedSearch);

  const fetchLessons = useCallback(
    (params: Partial<IPaginationParams>) => {
      if (isFetching) return;
      setFilters(filters => ({ ...filters, ...params }));
    },
    [isFetching]
  );

  useEffect(() => {
    if (!response || isFetching || !reachedEnd) return;

    if (response.total_pages > response.page.page) {
      setFilters(filters => ({
        ...filters,
        page: (filters?.page ?? 0) + 1
      }));
    }
  }, [reachedEnd, response, isFetching]);

  const handleSearch = (event: React.ChangeEvent<{ value: string }>) => {
    setFilters(filters => ({ ...filters, search: event.target.value || undefined, page: 1 }));
  };

  const handleOrder = (order: number) => {
    const orderMapper = [
      { order: 'ASC', orderBy: 'created_at' },
      { order: 'DESC', orderBy: 'created_at' },
      { order: 'ASC', orderBy: 'title' },
      { order: 'ASC', orderBy: 'course_title' }
    ];

    fetchLessons({ ...orderMapper[order], page: 1 });
  };

  const orderByMenu = ['Mais recentes', 'Mais antigos', 'Alfabética A-Z', 'Cursos'];
  const hasSavedLessons = response?.data.length > 0;

  return (
    <Wrapper className={props.className}>
      <PageTitle
        className='saved-lesson-titleSpace'
        title='Aulas Salvas'
        endContent={
          <Space size={16}>
            <Input size='large' placeholder='Buscar' prefix={<Search />} onChange={handleSearch} />

            <Select
              onChange={handleOrder}
              placeholder={<Typography.Text strong>Exibir por</Typography.Text>}
              options={orderByMenu.map((label, value) => ({ value, label }))}
              size='large'
            />
          </Space>
        }
      />

      {isFetching && (
        <Row gutter={DEFAULT_ROWS_SPACING}>
          {new Array(8).fill('').map((_, index) => (
            <Col {...DEFAULT_COLUMNS_WIDTH} key={index}>
              <SkeletonBaseCard aspect='16:9' />
            </Col>
          ))}
        </Row>
      )}

      {!hasSavedLessons && !isFetching && (
        <EmptyState
          icon={<Bookmark />}
          title='Suas aulas salvas aparecerão aqui...'
          description='Aqui você poderá acessar com facilidade todas as suas aulas salvas.'
        />
      )}

      {hasSavedLessons && !isFetching && (
        <Row gutter={DEFAULT_ROWS_SPACING}>
          {response.data.map(lesson => (
            <Col {...DEFAULT_COLUMNS_WIDTH} key={lesson.id}>
              <Card {...lesson} />
            </Col>
          ))}
        </Row>
      )}
    </Wrapper>
  );
};

export default styled(SavedLessons)`
  .ant-select {
    min-width: 150px;
  }
`;
